/**
 * BeraStays Google Maps style — Airbnb-inspired colourful palette.
 * Light, friendly, with real colours (water blue, parks green, roads white).
 */
export const beraMapStyle: google.maps.MapTypeStyle[] = [
  // Clean, minimalist background (Airbnb style)
  { elementType: "geometry", stylers: [{ color: "#f7f7f7" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f7f7f7" }, { weight: 3 }] },

  // Administrative
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#484848" }, { weight: "600" }],
  },
  {
    featureType: "administrative.neighborhood",
    elementType: "labels.text.fill",
    stylers: [{ color: "#767676" }],
  },

  // POIs — hide most to keep map extremely clean
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ color: "#eeeeee" }],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  // Parks — very subtle green
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#e8f3e5" }],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#618055" }],
  },

  // Roads — crisp white to stand out slightly from background
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road.arterial",
    elementType: "labels.text.fill",
    stylers: [{ color: "#757575" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#f0f0f0" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#e3e3e3" }],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#767676" }],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9e9e9e" }],
  },

  // Transit
  {
    featureType: "transit.line",
    elementType: "geometry",
    stylers: [{ color: "#e5e5e5" }],
  },
  {
    featureType: "transit.station",
    elementType: "geometry",
    stylers: [{ color: "#eeeeee" }],
  },

  // Water — vibrant cyan-blue that contrasts beautifully with white/gray land and red markers
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#a3ccff" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }],
  },
];

/**
 * Builds an SVG data URL for the BeraStays coral teardrop marker.
 * Returns a `google.maps.Icon`-compatible spec, lazily — call only after the
 * Maps JS API is loaded (so `google` is defined).
 */
export function getBeraMarkerIcon(size = 44): google.maps.Icon | undefined {
  if (typeof window === 'undefined' || !window.google || !window.google.maps) return undefined;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size * 1.15}" viewBox="0 0 44 50">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="hsl(12,82%,58%)"/>
          <stop offset="100%" stop-color="hsl(12,82%,46%)"/>
        </linearGradient>
        <filter id="s" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.35"/>
        </filter>
      </defs>
      <path d="M22 2 C12 2 4 10 4 20 C4 32 22 48 22 48 C22 48 40 32 40 20 C40 10 32 2 22 2 Z" fill="url(#g)" stroke="#fff" stroke-width="3" filter="url(#s)"/>
      <circle cx="22" cy="20" r="6" fill="#fff"/>
    </svg>
  `.trim();
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size * 1.15),
    anchor: new window.google.maps.Point(size / 2, size * 1.15),
  };
}

/** Small blue dot for "user location" marker. */
export function getUserDotIcon(): google.maps.Symbol | undefined {
  if (typeof window === 'undefined' || !window.google || !window.google.maps) return undefined;
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 8,
    fillColor: "#4285F4",
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 3,
  };
}

/** Destination flag for offer flow. */
export function getDestMarkerIcon(size = 40): google.maps.Icon | undefined {
  if (typeof window === 'undefined' || !window.google || !window.google.maps) return undefined;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size * 1.15}" viewBox="0 0 40 46">
      <defs>
        <linearGradient id="d" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#60a5fa"/>
          <stop offset="100%" stop-color="#2563eb"/>
        </linearGradient>
      </defs>
      <path d="M20 2 C11 2 4 9 4 18 C4 29 20 44 20 44 C20 44 36 29 36 18 C36 9 29 2 20 2 Z" fill="url(#d)" stroke="#fff" stroke-width="3"/>
      <circle cx="20" cy="18" r="5" fill="#fff"/>
    </svg>
  `.trim();
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size * 1.15),
    anchor: new window.google.maps.Point(size / 2, size * 1.15),
  };
}

export const DEFAULT_MAP_OPTIONS: google.maps.MapOptions = {
  styles: beraMapStyle,
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  rotateControl: false,
  scaleControl: false,
  keyboardShortcuts: false,
  clickableIcons: false,
  gestureHandling: "greedy",
  backgroundColor: "#f7f2ea",
};
